/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses, Box, Tooltip } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, DeleteRounded, PersonAddAlt, SaveRounded, TableViewOutlined } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './style';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { renderCell } from '../ui/Datagrid/table_config';
import { addNewSurveyRow, createSurvey, deleteSurveyRow, importWellsSurveyCSVFIle, listSurvey, updateSurvey } from '../../store/actions/wellsSurvey';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import FileUploadDialog from '../ui/FileUploadDialog';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import { APP_ACTION_TYPES, HYDRAULICS_ADMIN_ROLES, HYDRAULICS_MAX_FILE_SIZE, PERMISSION_DIALOG_SOURCES, TOOLTIPS } from '../../utils/constants';
import PermissionsModal from '../permissions';
import { checkPermissions } from '../../utils/checkPermissions';
import MDTVDInterpolationDialog from '../ui/MDTVDInterpolationDialog';
import CreatedByInfoDialog from '../ui/CreatedByInfoDialog';
import { REDUX_ACTIONS } from '../../shared/config/constants';
const SurveyModal = ({
  onCloseClick,
  surveyData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const survey = useSelector((state) => state.wellsSurvey);
  const user = useSelector((state) => state.currentUser);
  const apiRef = useGridApiRef();
  const [nonEditableFields, setNonEditableFields] = useState(false);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const disableAction = properties.some(item => item?.isDirty === true || item?.isNewRow);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = HYDRAULICS_ADMIN_ROLES.includes(user?.type);
  const [mdTvdInterpolation, setMdTvdInterpolation] = useState(false);

  useEffect(() => {
    if (!survey.loading) {
      dispatch(listSurvey(surveyData?.row?._id, onCloseClick));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.WELLS_SURVEY_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (survey?.data) {
      setProperties(survey?.data);
    }
  }, [survey?.data]);

  useEffect(() => {
    if (properties[0]?.md === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'md', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'md');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddSurvey = () => {
    dispatch(addNewSurveyRow());
  };

  const removeSurvey = (row) => {
    if (row?.isNewRow) {
      const updatedSurvey = properties.filter(item => item._id !== row?._id);
      setProperties(updatedSurvey);
      dispatch(listSurvey(surveyData?.row?._id));
    } else {
      dispatch(deleteSurveyRow(row?._id, surveyData?.row?._id));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const checkMD = (row, index, properties) => {
    if (index === 0 && (row.md !== 0 || row.inc !== 0 || row.az !== 0)) {
      return showError(`The first entered directional survey should only be 0.0 ${survey?.units?.md?.label}`, row._id);
    }
    if (index > 0 && row.md <= properties[index - 1].md) {
      return showError(`The measured depth at ${row.md} ${survey?.units?.md?.label} is less than the previous MD, please revise the input data`, row._id);
    }
    if (index < properties.length - 1 && row.md >= properties[index + 1].md) {
      return showError(`The measured depth at ${row.md} ${survey?.units?.md?.label} is greater than the next MD, please revise the input data`, row._id);
    }
    return true;
  };

  const checkInc = (row) => {
    const minInc = survey?.units?.inc?.min;
    const maxInc = survey?.units?.inc?.max;
    if (row.inc < minInc || row.inc > maxInc) {
      return showError(`The inclination at depth ${row.md} ${survey?.units?.md?.label} should be between 0 to 120 degrees. Please revise the input data`, row._id);
    }
    return true;
  };

  const checkAz = (row) => {
    const minAz = survey?.units?.az?.min;
    const maxAZ = survey?.units?.az?.max;
    if (row.az < minAz || row.az > maxAZ) {
      return showError(`The azimuth at depth ${row.md}  ${survey?.units?.md?.label} should be between 0 to 360 degrees. Please revise the input data`, row._id);
    }
    return true;
  };

  const validateSurveyData = (row) => {
    const rowIndex = properties.findIndex(item => item._id === row._id);
    const lastRow = properties[properties.length - 1];

    if (row.isNewRow) {
      if (properties.length === 1) {
        return row.md === 0 || showError('The first entered directional survey should only be 0.0', row._id);
      }
      if (!checkMD(row, properties.length, properties) || !checkInc(row, lastRow) || !checkAz(row)) {
        return false;
      }
    } else {
      if (!checkMD(row, rowIndex, properties) || !checkInc(row, rowIndex > 0 ? properties[rowIndex - 1] : null) || !checkAz(row)) {
        return false;
      }
    }
    return true;
  };

  const checkSurveyDataValues = (row) => {
    const invalidFields = [];
    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured Depth');
    if (row?.inc === undefined || row?.inc === null || row?.inc === '') invalidFields.push('Inclination');
    if (row?.az === undefined || row?.az === null || row?.az === '') invalidFields.push('Azimuth');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      for (const key in row) {
        if (typeof row[key] === 'string' && !isNaN(row[key])) {
          row[key] = Number(row[key]);
        }
      }
      return validateSurveyData(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveSurvey = (row) => {
    if (checkSurveyDataValues(row)) {
      const keysToExtract = ['md', 'inc', 'az'];

      const extracted = Object.fromEntries(
        Object.entries(row).filter(([key]) => keysToExtract.includes(key))
      );
        // eslint-disable-next-line multiline-ternary
      const converted = unit !== UNIT_SYSTEM.US ? {
        md: convertUnitSystem(extracted.md, survey?.units?.md.conversionFactor),
        inc: convertUnitSystem(extracted.inc, survey?.units?.inc.conversionFactor),
        az: convertUnitSystem(extracted.az, survey?.units?.az.conversionFactor),
      } : null;

      const payload = unit !== UNIT_SYSTEM.US ? converted : extracted;

      if (row?.isNewRow) {
        dispatch(createSurvey(payload, surveyData?.row?._id));
      } else {
        dispatch(updateSurvey(payload, surveyData?.row?._id, row?._id));
      }
    }
  };

  const handleOpenUploadModal = () => {
    setUploadOpen(true);
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    setProperties([]);
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {surveyData?.row?.name || ''} | Surveys <CreatedByInfoDialog data={survey?.createdDetails} loading={survey?.loading || disableAction} />
          </Grid>
          <Grid item>
          {isHydraulicsAdmin
            ? (
                <Tooltip title={TOOLTIPS.viewPermissions}>
                  <span>
                    <IconButton
                      variant="solid"
                      key="permissions"
                      disabled={survey?.loading || disableAction || properties?.length === 0}
                      sx={{ color: survey?.permissionId && 'green' }}
                      onClick={() => {
                        setPermissions({
                          open: true,
                          data: {
                            ...surveyData?.row,
                            _id: survey?._id,
                            permissionId: survey?.permissionId
                          }
                        });
                      }}
                    >
                      <PersonAddAlt fontSize='large' />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            : null}
          <Tooltip title={TOOLTIPS.mdTvdInterpolation}>
            <span>
              <IconButton
                disabled={properties.some(item => item?.isDirty === true) || survey?.data?.length <= 1 || survey?.loading}
                onClick={() => {
                  setMdTvdInterpolation(true);
                }}
              >
                <TableViewOutlined fontSize='large' />
              </IconButton>
            </span>
          </Tooltip>
            <IconButton
              onClick={closeHandler}
            >
              <CancelPresentationRounded fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
    );
  };
  const renderActions = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || survey?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, survey?.permissions))}
          onClick={() => {
            saveSurvey(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={survey?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, survey?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: Number(value),
            isDirty,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const handleUploadSuccess = () => {
    setUploadOpen(false);
  };

  const handleUploadCsv = (selectedFile) => {
    if (selectedFile.size >= HYDRAULICS_MAX_FILE_SIZE.ONE_MB_BYTE) {
      dispatch(
        enqueueSnackbar(
          'File must be less than 1 MB ',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    } else {
      dispatch(importWellsSurveyCSVFIle(selectedFile, surveyData?.row?._id, setIsLoading, handleUploadSuccess));
    }
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};
  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Box className={classes.customTool} sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, survey?.permissions) && 'none !important' }}>
          <Box>
          <Typography variant='button' fontWeight='700' className={survey.loading || disableAction ? classes.disableIcon : ''} onClick={handleAddSurvey}>+ Add</Typography>
          </Box>
          <Box>
          <Typography
            variant="body1"
            fontWeight="500"
            sx={{ marginLeft: '8px', marginRight: '8px', fontSize: '17px' }}
          >
            |
          </Typography>
          </Box>
          <Box>
          <label className={survey.loading || disableAction ? classes.disableImportLabel : classes.importLabel} onClick={() => {
            handleOpenUploadModal();
          }}>Import</label>
          </Box>
        </Box>
        <Grid item xs={12}>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removeSurvey}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        <UnsavedChangesModal />
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            loading={survey.loading}
            columns={columns(renderCell, renderActions, nonEditableFields, renderValueCell, survey.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            autoheight
            height={'50vh'}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={1000}
            onRowSelectionModelChange={(ids) => {
              const editableField = apiRef?.current.getSelectedRows().get(ids[0])?.isNewRow || false;
              setNonEditableFields(editableField);
            }}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={survey?.permissions}
          />
        </Grid>
        {uploadOpen && (
          <FileUploadDialog
          open={uploadOpen}
          handleClose={() => {
            setUploadOpen(false);
          }}
          isLoading={isLoading}
          handleUpload={handleUploadCsv}
          fileSize={HYDRAULICS_MAX_FILE_SIZE.ONE_MB}
        />
        )}
      </Grid>
      {permissions?.open
        ? (
          <PermissionsModal
            isOpen={permissions?.open}
            data={
              {
                ...permissions?.data,
                collection: PERMISSION_DIALOG_SOURCES?.wellsSurveys,
              }
            }
            onCloseClick={() => {
              setPermissions({
                open: false,
                data: {}
              });
            }}
            refreshParentDialog={() => {
              dispatch(listSurvey(surveyData?.row?._id));
            }}
          />
          )
        : null}
      {mdTvdInterpolation
        ? (
          <MDTVDInterpolationDialog
            isOpen={mdTvdInterpolation}
            onCloseClick={() => {
              setMdTvdInterpolation(false);
            }}
            data={survey}
          />
          )
        : null}
    </ModalDialog>
  );
};
SurveyModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  surveyData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
SurveyModal.defaultProps = {
  surveyData: {}
};
export default SurveyModal;
